import React, {
  ComponentType,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import {
  ImperativePanelHandle,
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';
import styles from '../../components/Document/styles.module.css';
import { SlidingPane } from '../../components/Page/SlidingPane';
import { SlidingPaneAddNew } from '../../components/Page/SlidingPaneAddNew';
import Table from '../../components/Table/NewTable';
import { useStateContext } from '../../hooks/useStateContext';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import { NotFound } from '../NotFound/NotFound';
import { toISODateString } from '../../services/toISODateString';
import { useParams } from 'react-router-dom';
import { SocketProvider } from '../../contexts/SocketContext';
import { ChangeTabConfirmDialog } from '../../components/SidePane/ChangeTabConfirmDialog';
import Box from '@mui/material/Box';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import api from '../../services/api';
import {
  AddNewActivityReservationWithStepper
} from "../../components/ActivityReservations/Stepper/AddNewActivityReservationWithStepper";

type Props = {
  schemaProvider: () => SchemaWithClient<Entity>;
  subSchemaProvider: () => SchemaWithClient<Entity>;
  viewName: string;
  subViewName: string;
  baseUrl: string;
};

export const Group: ComponentType<Props> = ({
  schemaProvider,
  subSchemaProvider,
  viewName,
  subViewName,
  baseUrl,
}) => {
  const { user, isMobile } = useStateContext();
  const { id } = useParams();

  const [loading, setLoading] = useState<any>({
    table: false,
  });
  const [view, setView] = useState<any>(undefined);
  const [subView, setSubView] = useState<any>(undefined);
  const { viewPeriod } = useStateContext();
  const [width, setWidth] = useState(40);
  const [selectedRow, setSelectedRow] = useState<any>(undefined);
  const [selectedSubRow, setSelectedSubRow] = useState<any>(undefined);

  const schema = useMemo(schemaProvider, []);

  const subSchema = useMemo(subSchemaProvider, []);

  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({
    schema: schema,
    row: {},
  });
  const [openedSlidingPaneAddNew, setOpenedSlidingPaneAddNew] = useState<any>({
    schema: schema,
    row: {},
    newPanel: false,
  });
  const [fullWidth, setFullWidth] = useState(false);

  const [columnMask, setColumnMask] = useState(undefined);
  const [subColumnMask, setSubColumnMask] = useState(undefined);
  const [isLocked, setIsLocked] = useState({
    groups: false,
    activity_reservations: false,
  });

  const [unsavedModification, setUnsavedModification] = useState(false);
  const [
    openModificationConfirmDialog,
    setOpenModificationConfirmDialog,
  ] = useState<any>(false);

  const searchParams = useMemo(() => {
    const searchParams = new URLSearchParams();
    if (viewPeriod[0]) {
      searchParams.append('from', toISODateString(viewPeriod[0]));
    }
    if (viewPeriod[1]) {
      searchParams.append('to', toISODateString(viewPeriod[1]));
    }
    return searchParams;
  }, [viewPeriod]);

  useEffect(() => {
    setLoading({ ...loading, ['table']: true });

    const viewPromise = api
      .get(`/users/${user.id}/views/${viewName}`)
      .then((res: any) => {
        setView(res);

        const columnMaskObj = res.columns.reduce((acc: any, column: any) => {
          return {
            ...acc,
            [column.name]: true,
          };
        }, {});
        columnMaskObj[`__tree_data_group__`] = false;
        setColumnMask(columnMaskObj);
      });

    const subViewPromise = api
      .get(`/users/${user.id}/views/${subViewName}`)
      .then((res: any) => {
        setSubView(res);

        const columnMaskObj = res.columns.reduce((acc: any, column: any) => {
          return {
            ...acc,
            [column.name]: true,
          };
        }, {});
        columnMaskObj[`__tree_data_group__`] = false;
        setSubColumnMask(columnMaskObj);
      });

    Promise.all([viewPromise, subViewPromise]).finally(() =>
      setLoading({ ...loading, ['table']: false })
    );
  }, []);

  const panelRef: any = useRef(null);

  const handlePanelWidth = (fullWidth: boolean) => {
    setFullWidth(fullWidth);
    if (panelRef.current) {
      panelRef.current.resize(fullWidth ? 100 : width);
    }
  };

  const openSlidingPaneHandle = (
    row: any,
    openedSchema: any,
    newEntity?: boolean,
    openInFull?: boolean,
    isLocked?: any,
    newPane?: any
  ) => {
    if (openedSchema.name === 'Groups') {
      if (isLocked !== undefined) {
        setIsLocked({ ...isLocked, groups: isLocked });
      }
      if (row?.id) {
        clearUnreadGroup(row.id);
      }
    } else {
      if (isLocked !== undefined) {
        setIsLocked({ ...isLocked, activity_reservations: isLocked });
      }
    }

    if (newEntity) {
      setOpenedSlidingPaneAddNew({
        schema: openedSchema,
        row,
        newPane: !!newPane,
      });
      handlePanelWidth(!!openInFull);
      return;
    }

    setOpenedSlidingPane({
      schema: openedSchema,
      row,
    });

    handlePanelWidth(!!openInFull);
  };

  useEffect(() => {
    document.title = `Zoe - Groups`;
  }, []);

  const mainFunctionRef: any = useRef(null);
  const childFunctionRef: any = useRef(null);

  const slidingPaneRef = useRef<any>(null);

  const handleRefreshMainTableData = () => {
    console.log('handleRefreshMainTableData');
    mainFunctionRef.current();
  };
  const handleRefreshChildTableData = () => {
    console.log('handleRefreshChildTableData');
    childFunctionRef.current();
  };

  useEffect(() => {
    if (id) {
      handlePanelWidth(true);
      schema.client.readOne(parseInt(id), searchParams).then((res: any) => {
        setOpenedSlidingPane({
          schema: schema,
          row: res,
        });
      });
    }
  }, [id]);

  const [unreadGroupIds, setUnreadGroupIds] = useState<Set<number>>(new Set());

  const handleNewMessage = (groupId: number) => {
    setUnreadGroupIds(prev => {
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
  };

  const clearRowNotificationRef = useRef<((rowId: number) => void) | null>(null);

  const clearUnreadGroup = (groupId: number) => {
    setUnreadGroupIds(prev => {
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
    api.post('/notifications/group', { ids: [groupId], value: true, type: 'group-message' });
    clearRowNotificationRef.current?.(groupId);
  };

  const [collapsed, setCollapsed] = useState(false);
  const minTopPanelHeight = 180;
  const minBottomPanelHeight = 70;
  const totalHeight = window.innerHeight;

  const minTopPanelSize = (minTopPanelHeight / totalHeight) * 100;
  const minBottomPanelSize = (minBottomPanelHeight / totalHeight) * 100;

  const bottomPanelRef = useRef<ImperativePanelHandle>(null);

  const resizePanels = useCallback(
    (row: any, rowCount = row?.activity_reservation_count || 0) => {
      if (!bottomPanelRef.current) {
        return;
      }

      if (collapsed) {
        bottomPanelRef.current.resize(minBottomPanelSize);
        return;
      }

      if (!row) {
        bottomPanelRef.current.resize(0.1);
      }

      const defaultHeight = window.innerWidth < 1400 ? 24 : 22;

      const minSizeWithOutRows = 120;
      const newBottomPanelHeight = minSizeWithOutRows + defaultHeight * rowCount;

      const newBottomSize = (newBottomPanelHeight / totalHeight) * 100;
      
      bottomPanelRef.current.resize(newBottomSize);
    },
    [bottomPanelRef, collapsed]
  );

  useEffect(() => {
    resizePanels(selectedRow);
  }, [collapsed]);

  if (loading.table) {
    return <LoadingPage />;
  }

  if (!schema || !view) {
    return <LoadingPage />;
  }

  const handleKickEvent = (data: any) => {
    console.log('Kick event received in parent component:', data);

    if (data.table === schema?.viewTable) {
      if (slidingPaneRef.current) {
        slidingPaneRef.current.onSubmit();
      }
    }
  };

  const handleSelectRow = (row: any) => {
    setSelectedRow(row);

    if (!row) {
      return;
    }
    resizePanels(row);
  };

  const handleSelectNewRow = (
    row: any,
    openedSchema: any,
    newEntity?: boolean,
    openInFull?: boolean,
    isLocked?: any,
    modification?: any
  ) => {
    if (modification) {
      setOpenModificationConfirmDialog({
        row: row,
        openedSchema: openedSchema,
        newEntity: newEntity,
        openInFull: openInFull,
        isLocked: isLocked,
      });
    } else {
      openSlidingPaneHandle(row, openedSchema, false, openInFull, isLocked);
    }
  };

  return (
    <>
      <SocketProvider
        schema={schema}
        subSchema={subSchema}
        onKick={handleKickEvent}
        onNewMessage={handleNewMessage}
      >
        {isMobile ? (
          <>
            <Box>
              <Table
                setFunctionRef={(ref: any) => (mainFunctionRef.current = ref)}
                searchParams={searchParams}
                columnMask={columnMask}
                schema={schema}
                onRowSelected={setSelectedRow}
                selectedRowId={selectedRow}
                isView={true}
                viewId={view.id}
                pins={view?.pins}
                currentEditedRow={openedSlidingPane}
                setCurrentEditedRow={(
                  row: any,
                  openInFull?: boolean,
                  isLocked?: any
                ) => {
                  //openSlidingPaneHandle(row, schema, false, openInFull,isLocked);
                  handleSelectNewRow(
                    row,
                    schema,
                    false,
                    openInFull,
                    isLocked,
                    unsavedModification
                  );
                }}
                setCurrentAddNewRow={(row: any) => {
                  openSlidingPaneHandle(row, schema, true);
                }}
                hasSlidingPane={true}
                openedPanel={openedSlidingPane?.row?.id}
                unreadRowIds={unreadGroupIds}
                onChatOpened={(rowId) => clearUnreadGroup(rowId)}
                clearNotificationRef={(fn) => { clearRowNotificationRef.current = fn; }}
              />
            </Box>

            {selectedRow && (
              <Box>
                <Table
                  setFunctionRef={(ref: any) =>
                    (childFunctionRef.current = ref)
                  }
                  searchParams={new URLSearchParams()}
                  columnMask={subColumnMask}
                  schema={subSchema}
                  onRowSelected={setSelectedSubRow}
                  isView={false}
                  viewId={subView.id}
                  pins={subView?.pins}
                  selectedRowId={selectedSubRow}
                  selectedGroupId={selectedRow}
                  setCurrentEditedRow={(
                    row: any,
                    openInFull?: boolean,
                    isLocked?: any
                  ) => {
                    handleSelectNewRow(
                      row,
                      subSchema,
                      false,
                      openInFull,
                      isLocked,
                      unsavedModification
                    );
                  }}
                  setCurrentAddNewRow={(row: any) => {
                    openSlidingPaneHandle(row, subSchema, true, false);
                  }}
                  hasSlidingPane={true}
                  openedPanel={openedSlidingPane?.row?.id}
                />
              </Box>
            )}
          </>
        ) : (
          <>
            <PanelGroup direction="horizontal">
              <Panel>
                <PanelGroup key={1} autoSaveId="example" direction="vertical">
                  <Panel
                    className={`${selectedRow ? '' : 'full-height'} ${
                      styles.Panel
                    }`}
                    collapsible={false}
                    minSize={minTopPanelSize}
                    defaultSize={100}
                    order={1}
                  >
                    <Table
                      setFunctionRef={(ref: any) =>
                        (mainFunctionRef.current = ref)
                      }
                      searchParams={searchParams}
                      columnMask={columnMask}
                      schema={schema}
                      onRowSelected={(row: any) => {
                        handleSelectRow(row);
                      }}
                      onShowReservationsChange={() => {
                        setCollapsed(!collapsed);
                      }}
                      selectedRowId={selectedRow}
                      isView={true}
                      viewId={view.id}
                      pins={view?.pins}
                      currentEditedRow={openedSlidingPane}
                      setCurrentEditedRow={(
                        row: any,
                        openInFull?: boolean,
                        isLocked?: any
                      ) => {
                        //openSlidingPaneHandle(row, schema, false, openInFull,isLocked);
                        handleSelectNewRow(
                          row,
                          schema,
                          false,
                          openInFull,
                          isLocked,
                          unsavedModification
                        );
                      }}
                      setCurrentAddNewRow={(row: any) => {
                        openSlidingPaneHandle(row, schema, true);
                      }}
                      hasSlidingPane={true}
                      openedPanel={openedSlidingPane?.row?.id}
                      unreadRowIds={unreadGroupIds}
                      onChatOpened={(rowId) => clearUnreadGroup(rowId)}
                      clearNotificationRef={(fn) => { clearRowNotificationRef.current = fn; }}
                    />
                  </Panel>

                  <>
                    {selectedRow && (
                      <PanelResizeHandle
                        className={`${styles.ResizeHandleOuter}`}
                        id={'asd'}
                      >
                        <div className={styles.ResizeHandleInner}>
                          <svg className={styles.Icon} viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"
                            />
                          </svg>
                        </div>
                      </PanelResizeHandle>
                    )}
                    <Panel
                      className={`${styles.Panel} justify-end`}
                      minSize={0.1}
                      defaultSize={0.1}
                      ref={bottomPanelRef}
                      collapsible={true}
                      order={2}
                    >
                      {selectedRow && (
                        <div className="max-h-full">
                          <Table
                            onRowCountChange={(rowCount) => {
                                //resizePanels(selectedRow, rowCount)
                              }
                            }
                            setFunctionRef={(ref: any) =>
                              (childFunctionRef.current = ref)
                            }
                            showReservations={collapsed}
                            onShowReservationsChange={() => {
                              setCollapsed(!collapsed);
                            }}
                            searchParams={new URLSearchParams()}
                            columnMask={subColumnMask}
                            schema={subSchema}
                            onRowSelected={setSelectedSubRow}
                            isView={false}
                            viewId={subView.id}
                            pins={subView?.pins}
                            selectedRowId={selectedSubRow}
                            selectedGroupId={selectedRow}
                            setCurrentEditedRow={(
                              row: any,
                              openInFull?: boolean,
                              isLocked?: any
                            ) => {
                              handleSelectNewRow(
                                row,
                                subSchema,
                                false,
                                openInFull,
                                isLocked,
                                unsavedModification
                              );
                            }}
                            setCurrentAddNewRow={(row: any) => {
                              openSlidingPaneHandle(
                                row,
                                subSchema,
                                true,
                                false,
                              );
                            }}
                            setCurrentAddNewRowFastAdd={(row: any) => {
                              openSlidingPaneHandle(
                                row,
                                subSchema,
                                true,
                                false,
                                false,
                                true
                              );
                            }}
                            hasSlidingPane={true}
                            openedPanel={openedSlidingPane?.row?.id}
                          />
                        </div>
                      )}
                    </Panel>
                  </>
                </PanelGroup>
              </Panel>
              {openedSlidingPane?.row?.id && (
                <>
                  <PanelResizeHandle
                    className={`${styles.ResizeHandleOuter} bg-[#ddd]`}
                  >
                    <div className={styles.ResizeHandleInner}>
                      <svg className={styles.Icon} viewBox="0 0 24 24">
                        <path
                          fill="currentColor"
                          d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"
                        />
                      </svg>
                    </div>
                  </PanelResizeHandle>
                  <Panel
                    className={`${styles.Panel}`}
                    collapsible={false}
                    minSize={20}
                    defaultSize={fullWidth ? 100 : 40}
                    ref={panelRef}
                  >
                    <SlidingPane
                      ref={slidingPaneRef}
                      setFullWidthProp={(value) => {
                        handlePanelWidth(value);
                      }}
                      fullWidth={fullWidth}
                      schema={openedSlidingPane?.schema}
                      id={
                        openedSlidingPane?.hasOwnPane
                          ? undefined
                          : openedSlidingPane?.row?.id
                      }
                      onSubmit={() => {
                        if (
                          openedSlidingPane?.schema?.viewTable ===
                          'activity_reservations'
                        ) {
                          handleRefreshChildTableData();
                        } else {
                          handleRefreshMainTableData();
                        }
                      }}
                      propertiesFromTable={
                        openedSlidingPane?.row ? openedSlidingPane?.row : {}
                      }
                      onClose={(value) => {
                        if (
                          openedSlidingPane?.schema?.viewTable ===
                          'activity_reservations'
                        ) {
                          subSchema.client.deleteLock(
                            openedSlidingPane?.row?.id,
                            {
                              schema: JSON.stringify(
                                openedSlidingPane?.schema.client
                              ),
                              view_table: openedSlidingPane?.schema?.viewTable,
                            }
                          );
                        } else {
                          schema.client.deleteLock(openedSlidingPane?.row?.id, {
                            schema: JSON.stringify(
                              openedSlidingPane?.schema.client
                            ),
                            view_table: openedSlidingPane?.schema?.viewTable,
                          });
                        }
                        setOpenedSlidingPane({
                          schema: schema,
                          row: {},
                        });
                      }}
                      addNewRow={(schema: any, tab: any) => {
                        openSlidingPaneHandle(
                          {
                            id: 'new',
                            group: openedSlidingPane?.row,
                          },
                          schema,
                          true,
                          false
                        );
                      }}
                      refreshMainTable={() => {
                        handleRefreshMainTableData();
                      }}
                      isLocked={
                        openedSlidingPane?.viewTable === 'groups'
                          ? isLocked.groups
                          : isLocked.activity_reservations
                      }
                      hasUnsavedModifications={(value: any) => {
                        console.log(`Group unsaved changes: ${value}`);
                        setUnsavedModification(value);
                      }}
                    />
                  </Panel>
                </>
              )}
            </PanelGroup>
          </>
        )}

        {openedSlidingPaneAddNew?.row?.id && (
          <>
            {openedSlidingPaneAddNew?.newPane ? (
              <>
                <AddNewActivityReservationWithStepper
                  onClose={() => {
                    setOpenedSlidingPaneAddNew({
                      schema: schema,
                      row: {},
                    });
                  }}
                  onSubmit={() => {
                    setOpenedSlidingPaneAddNew({
                      schema: schema,
                      row: {},
                    });
                    handleRefreshMainTableData();
                  }}
                  data={selectedRow}
                />
              </>
            ) : (
              <SlidingPaneAddNew
                schema={openedSlidingPaneAddNew?.schema}
                id={
                  openedSlidingPaneAddNew?.hasOwnPane
                    ? undefined
                    : openedSlidingPaneAddNew?.row?.id
                }
                onSubmit={() => {
                  setOpenedSlidingPaneAddNew({
                    schema: schema,
                    row: {},
                  });
                  handleRefreshMainTableData();
                }}
                propertiesFromTable={
                  openedSlidingPaneAddNew?.row
                    ? openedSlidingPaneAddNew?.row
                    : {}
                }
                onClose={(value) => {
                  setOpenedSlidingPaneAddNew({
                    schema: schema,
                    row: {},
                  });
                }}
              />
            )}
          </>
        )}
        {openModificationConfirmDialog && (
          <ChangeTabConfirmDialog
            onClose={() => {
              setOpenModificationConfirmDialog(false);
            }}
            onSubmit={() => {
              if (slidingPaneRef.current) {
                slidingPaneRef.current.onSubmit();
              }
              handleSelectNewRow(
                openModificationConfirmDialog?.row,
                openModificationConfirmDialog?.openedSchema,
                openModificationConfirmDialog?.newEntity,
                openModificationConfirmDialog?.openInFull,
                openModificationConfirmDialog?.isLocked,
                false
              );
              setOpenModificationConfirmDialog(false);
            }}
            onCloseWithOutSaving={() => {
              handleSelectNewRow(
                openModificationConfirmDialog?.row,
                openModificationConfirmDialog?.openedSchema,
                openModificationConfirmDialog?.newEntity,
                openModificationConfirmDialog?.openInFull,
                openModificationConfirmDialog?.isLocked,
                false
              );
              setOpenModificationConfirmDialog(false);
            }}
          />
        )}
      </SocketProvider>
    </>
  );
};
